import React from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Shield, Check, X, CircleHelp } from "lucide-react";
import { getStatusBadge } from "./ClaimsUtils";
import { NursingHomeClaim, NursingHomeClaimActivityRow } from "@/types/admin";
import { ScrollArea } from "@/components/ui/scroll-area";
import { canAdminActOnClaim, showRequestMoreInfoAction } from "@/lib/claim-status";

function formatActivityLine(row: NursingHomeClaimActivityRow): string {
  const t = new Date(row.created_at).toLocaleString("en-IE");
  const d = row.details && typeof row.details === "object" ? row.details : {};
  const before = d.status_before;
  const after = d.status_after;
  if (row.event_type === "submitted") {
    return `${t} — ${row.actor_role}: claim submitted`;
  }
  if (
    row.event_type === "updated" &&
    typeof before === "string" &&
    typeof after === "string" &&
    before !== after
  ) {
    return `${t} — ${row.actor_role}: ${before} → ${after}`;
  }
  return `${t} — ${row.actor_role}: ${row.event_type}`;
}

interface ClaimDetailsCardProps {
  claim: NursingHomeClaim;
  activity?: NursingHomeClaimActivityRow[];
  activityLoading?: boolean;
  adminNotes: string;
  setAdminNotes: (s: string) => void;
  onApprove: () => void;
  onReject: () => void;
  onNeedsInfo: () => void;
  onClose: () => void;
}

const ClaimDetailsCard: React.FC<ClaimDetailsCardProps> = ({
  claim,
  activity = [],
  activityLoading = false,
  adminNotes,
  setAdminNotes,
  onApprove,
  onReject,
  onNeedsInfo,
  onClose,
}) => (
  <Card className="bg-white shadow-sm border p-8 mb-8">
    <CardHeader className="pb-6">
      <div className="flex items-start justify-between">
        <CardTitle className="text-2xl font-bold flex items-center gap-2">
          <Shield className="h-6 w-6 text-blue-500" /> Claim Details
        </CardTitle>
        {getStatusBadge(claim.status)}
      </div>
      <CardDescription className="text-base mt-2">
        Submitted on {claim.created_at ? new Date(claim.created_at).toLocaleDateString() : ""}
      </CardDescription>
    </CardHeader>
    <CardContent className="space-y-8 divide-y divide-muted">
      <div className="pb-8">
        <h3 className="text-lg font-semibold mb-1 flex items-center gap-2">
          <Shield className="h-5 w-5 text-blue-500" /> Nursing Home
        </h3>
        <p className="text-base">{claim.nursing_home?.center_name}</p>
        <p className="text-sm text-muted-foreground">{claim.nursing_home?.county}</p>
      </div>

      <div className="pt-8 pb-8">
        <h3 className="text-lg font-semibold mb-1">Claimant Information</h3>
        <p className="text-base">
          {(claim.user?.user_metadata?.first_name || "") + " " + (claim.user?.user_metadata?.last_name || "")}
        </p>
        <p className="text-sm text-muted-foreground">{claim.user?.email}</p>
      </div>

      <div className="pt-8 pb-8">
        <h3 className="text-lg font-semibold mb-1">Role & Contact</h3>
        <p className="text-base">{claim.verification_documents?.role || "Not specified"}</p>
        <p className="text-sm text-muted-foreground">
          Phone: {claim.verification_documents?.contactPhone || "Not provided"}
        </p>
      </div>

      {claim.verification_documents?.claimant_reply && (
        <div className="pt-8 pb-8">
          <h3 className="text-lg font-semibold mb-1">Latest claimant reply</h3>
          <p className="text-base bg-emerald-50 border border-emerald-100 p-4 rounded whitespace-pre-wrap">
            {claim.verification_documents.claimant_reply}
          </p>
          {claim.verification_documents.claimant_replied_at && (
            <p className="text-xs text-muted-foreground mt-2">
              Submitted: {String(claim.verification_documents.claimant_replied_at)}
            </p>
          )}
        </div>
      )}

      {claim.notes && (
        <div className="pt-8 pb-8">
          <h3 className="text-lg font-semibold mb-1">Message on file (claimant-visible)</h3>
          <p className="text-base bg-muted/20 p-4 rounded">{claim.notes}</p>
        </div>
      )}

      <div className="pt-8 pb-8">
        <h3 className="text-lg font-semibold mb-2">Activity</h3>
        {activityLoading ? (
          <p className="text-sm text-muted-foreground">Loading history…</p>
        ) : activity.length === 0 ? (
          <p className="text-sm text-muted-foreground">No history yet.</p>
        ) : (
          <ScrollArea className="h-48 rounded-md border bg-muted/30 p-3">
            <ul className="space-y-2 text-sm">
              {activity.map((row) => (
                <li key={row.id} className="leading-snug">
                  {formatActivityLine(row)}
                </li>
              ))}
            </ul>
          </ScrollArea>
        )}
      </div>

      {canAdminActOnClaim(claim.status) && (
        <div className="pt-8">
          <Label htmlFor="adminNotes" className="text-base font-semibold">
            {showRequestMoreInfoAction(claim.status)
              ? "Message to claimant (optional for approve/reject; required for “Request more info”)"
              : "Message to claimant"}
          </Label>
          <Textarea
            id="adminNotes"
            placeholder="Notes shown to the claimant in their email and dashboard where relevant"
            className="mt-2"
            value={adminNotes}
            onChange={e => setAdminNotes(e.target.value)}
          />
        </div>
      )}
    </CardContent>
    <CardFooter className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end pt-8">
      {canAdminActOnClaim(claim.status) && (
        <div className="flex flex-col gap-2 w-full sm:flex-row sm:flex-wrap sm:justify-end">
          <Button variant="destructive" onClick={onReject}>
            <X className="h-4 w-4 mr-1" /> Reject
          </Button>
          {showRequestMoreInfoAction(claim.status) && (
            <Button variant="secondary" onClick={onNeedsInfo}>
              <CircleHelp className="h-4 w-4 mr-1" /> Request more info
            </Button>
          )}
          <Button variant="default" onClick={onApprove}>
            <Check className="h-4 w-4 mr-1" /> Approve
          </Button>
        </div>
      )}
      <Button variant="outline" onClick={onClose}>
        Close
      </Button>
    </CardFooter>
  </Card>
);

export default ClaimDetailsCard;
